import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { casinoGames } from '../../UI-components/Sections/CasinoGamesSection/casino-games'
import { RootState } from '../../redux/store'
import { cleanEvent, EventData, eventInitialData } from '../../UI-components/Modals/CashierModal/CashierModal'
import { BRAND_KEY } from '../../core/const'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import { EventName } from '../../core/enums/moneyOperations'
import { useTranslation } from '../../hooks/useTranslation'
import { setCurrentGameExtId } from '../../redux/slices/gameSlice'
import Utils from '../../core/Utils'
import { GAME_WIDGET_URLS } from '../../core/widgets'
import WidgetGame from './WidgetGame'

interface GamePageProps {
	onSubmit: (eventName?: EventName, eventData?: any, close?: boolean) => void
}

const GamePage: React.FC<GamePageProps> = ({ onSubmit }) => {
	const { ext_id } = useParams()
	const { getTexts } = useTranslation(true)
	const dispatch = useAppDispatch()
	const isMobile = Utils.isMobile()

	const wallet = useAppSelector((state: RootState) => state.wallet)

	const [eventData, setEventData] = useState<EventData>(eventInitialData)
	const [betAmount, setBetAmount] = useState(10)
	const [winAmount, setWinAmount] = useState(0)
	const [selectedMultiplier, setSelectedMultiplier] = useState<number>(3)
	const [currentState, setCurrentState] = useState<string>('static')
	const [currentImage, setCurrentImage] = useState<string>('')
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [nextImage, setNextImage] = useState<string>('')
	const [isSpinning, setIsSpinning] = useState(false)
	const [showWin, setShowWin] = useState(false)

	const [isPortrait, setIsPortrait] = useState(window.screen.orientation.type.includes('portrait') && isMobile)

	useEffect(() => {
		const handleOrientationChange = () => {
			setIsPortrait(window.screen.orientation.type.includes('portrait'))
		}

		window.screen.orientation.addEventListener('change', handleOrientationChange)
		window.addEventListener('orientationchange', handleOrientationChange)

		return () => {
			window.screen.orientation.removeEventListener('change', handleOrientationChange)
			window.removeEventListener('orientationchange', handleOrientationChange)
		}
	}, [])

	useEffect(() => {
		if (ext_id) {
			dispatch(setCurrentGameExtId(ext_id))
		}
		return () => {
			dispatch(setCurrentGameExtId(null))
		}
	}, [ext_id, dispatch])

	useEffect(() => {
		const game = casinoGames.find((game) => game.ext_id === ext_id)
		if (game) {
			setCurrentImage(game.static_img)
			setCurrentState('static')
		}
	}, [ext_id])

	const determineNextState = (outcome: 'win' | 'loss') => {
		if (currentState === 'static' || currentState === 'loss-to-loss') {
			return outcome === 'win' ? 'loss-to-win' : 'loss-to-loss'
		} else if (currentState === 'win-to-win') {
			return outcome === 'win' ? 'win-to-win' : 'win-to-loss'
		} else if (currentState === 'win-to-loss') {
			return outcome === 'win' ? 'win-to-win' : 'loss-to-loss'
		} else if (currentState === 'loss-to-win') {
			return outcome === 'win' ? 'win-to-win' : 'loss-to-loss'
		}
		return 'static'
	}

	const stateToKeyMap: { [key: string]: keyof (typeof casinoGames)[0] } = {
		'static': 'static_img',
		'loss-to-loss': 'loss_to_loss',
		'loss-to-win': 'loss_to_win',
		'win-to-loss': 'win_to_loss',
		'win-to-win': 'win_to_win',
	}

	const sendEvent = (eventName: EventName, payload: any = {}, close: boolean = true) => {
		if (isSpinning || betAmount === 0) return
		setIsSpinning(true)

		let win = Math.floor(betAmount * (selectedMultiplier as number))

		const game = casinoGames.find((game) => game.ext_id === ext_id)
		if (game) {
			const outcome = win > 0 ? 'win' : 'loss'
			const nextState = determineNextState(outcome)
			setCurrentState(nextState)
			const imageKey = stateToKeyMap[nextState]
			const newImage = `${game[imageKey]}?t=${new Date().getTime()}`
			setCurrentImage(newImage)
		}

		setWinAmount(win)
		setEventData({ ...eventData, win })

		setTimeout(() => {
			setShowWin(true)
			setIsSpinning(false)
			setTimeout(() => {
				setShowWin(false)
				setWinAmount(0)
			}, 2000)
		}, 2000)

		const data = cleanEvent(eventName, { ...eventData, win, ...payload, brand_key: BRAND_KEY })
		onSubmit(eventName, data, close)
	}

	const multiplierOptions = [0, 1, 2, 3, 4, 5]

	const handleMultiplierChange = (direction: number) => {
		const currentIndex = multiplierOptions.indexOf(selectedMultiplier)
		const nextIndex = currentIndex + direction

		if (nextIndex >= 0 && nextIndex < multiplierOptions.length) {
			setSelectedMultiplier(multiplierOptions[nextIndex])
		}
	}

	const game = casinoGames.find((game) => game.ext_id === ext_id)

	if (!game) {
		return <div className="main-content game-page">{getTexts('game_not_found')}</div>
	}

	const widgetUrl = GAME_WIDGET_URLS[game.ext_id]

	if (widgetUrl) {
		return <WidgetGame gameId={game.ext_id} gameName={game.name} widgetUrl={widgetUrl} onSubmit={onSubmit} />
	}

	if (isPortrait) {
		return (
			<div className="rotate-device-screen">
				<div className="icon"></div>Please rotate your device.
			</div>
		)
	}

	return (
		<div className="main-content game-page">
			<div className="game-view-main">
				<div className="game-background">
					<Link to={'/'} className="btn home">
						<div className="icon"></div>
						{getTexts('lobby')}
					</Link>
					<img src={`${process.env.PUBLIC_URL}/${currentImage}`} alt="Game Background" className={`game-image ${nextImage ? 'fade-out' : ''}`} />
					{nextImage && <img src={`${process.env.PUBLIC_URL}/${nextImage}`} alt="Next Background" className="game-image fade-in" />}
					<div className="game-controls">
						<div className="multiplier-dropdown">
							<span>{getTexts('multiplier')}</span>
							<div className="wrapper">
								<button onClick={() => handleMultiplierChange(-1)} disabled={selectedMultiplier === 0}>
									-
								</button>
								<span>{selectedMultiplier}</span>
								<button onClick={() => handleMultiplierChange(1)}>+</button>
							</div>
						</div>
						<div className="multiplier-control">
							<span>{getTexts('bet')}</span>
							<div className="wrapper">
								<button onClick={() => setBetAmount(Math.max(0, betAmount - 5))}>-</button>
								<span>{betAmount}</span>
								<button onClick={() => setBetAmount(betAmount + 5)}>+</button>
							</div>
						</div>
						<div
							className={`spin-button ${isSpinning || betAmount === 0 ? 'disabled' : ''}`}
							onClick={() =>
								!isSpinning &&
								sendEvent(EventName.casino_bet_win, {
									bet: betAmount,
									game_name: ext_id,
								})
							}
						/>
						<div className="balance-display">
							<span>{getTexts('balance')}</span>
							<div className="wrapper">
								<span>{wallet.balance.toFixed(2)}</span>
							</div>
						</div>
						<div className="win-display">
							<span>{getTexts('wins')}</span>
							<div className="wrapper">
								<span>{showWin ? winAmount : '-'}</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

export default GamePage
